import { useState } from 'react';
import { ThemeGallery } from '../generation/ThemeGallery';
import { useSlideThemes } from '../generation/useSlideThemes';
import { useVibes } from '../generation/useVibes';
import { SectionShell } from './SectionShell';
import { useBrandDefaultTheme } from './useBrandDefaultTheme';

/**
 * The look catalogue as a browse surface: every theme the deployment seeds, and
 * the one decision this screen exists for — which of them new rendered posts
 * start in.
 *
 * Selecting a card here *is* setting the brand default; there is nothing else a
 * selection could mean on a screen with no brief attached to it. The per-card
 * button says so out loud for anyone who does not read a highlighted card as a
 * saved preference.
 *
 * Nothing here spends quota or calls a model — a look is a rendering recipe, and
 * choosing one only changes what the next run is drawn with.
 */
export function LooksSection({ businessId }: { businessId: string }) {
  const [vibeId, setVibeId] = useState<string | null>(null);
  const { vibes } = useVibes();
  const { themes, isLoading, error } = useSlideThemes(vibeId);
  const brandDefault = useBrandDefaultTheme(businessId);

  return (
    <SectionShell
      title="Visual style"
      lede="How your images and carousels look. Pick your brand's usual one — you can change it for any post."
      isLoading={isLoading}
      error={error ?? brandDefault.error}
      isEmpty={themes.length === 0}
      emptyMessage={
        vibeId
          ? 'No visual styles are tagged for that vibe. Clear the filter to see the whole catalogue.'
          : 'No visual styles added yet. Posts use the classic style until some are added.'
      }
    >
      <div className="looks">
        <div className="looks__filter" role="radiogroup" aria-label="Filter looks by vibe">
          <label className={vibeId === null ? 'chip chip--selected' : 'chip'}>
            <input
              className="chip__input"
              type="radio"
              name="looks-vibe"
              value=""
              checked={vibeId === null}
              onChange={() => setVibeId(null)}
            />
            <span>All vibes</span>
          </label>
          {vibes.map((vibe) => (
            <label className={vibeId === vibe.id ? 'chip chip--selected' : 'chip'} key={vibe.id}>
              <input
                className="chip__input"
                type="radio"
                name="looks-vibe"
                value={vibe.id}
                checked={vibeId === vibe.id}
                onChange={() => setVibeId(vibe.id)}
              />
              <span>{vibe.name}</span>
            </label>
          ))}
        </div>

        {brandDefault.notice ? (
          <p className="looks__notice" role="status">
            {brandDefault.notice}
          </p>
        ) : (
          <p className="looks__hint">
            The highlighted style is your brand default. Changing it affects the next post we render
            for you, never one already drawn.
          </p>
        )}

        <ThemeGallery
          themes={themes}
          selectedId={brandDefault.defaultThemeId}
          onSelect={(themeId) => {
            const picked = themes.find((theme) => theme.id === themeId);
            if (picked) brandDefault.setDefault(picked);
          }}
          brandDefaultId={brandDefault.defaultThemeId}
          onSetBrandDefault={brandDefault.setDefault}
          setDefaultVariant="primary"
        />
      </div>
    </SectionShell>
  );
}
